import React, { useEffect, useState, useCallback } from "react";
import { MainLayout } from "../MainLayout";
import { supabase } from "../../lib/supabase_client";
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import '../../styles/page-base.css';

dayjs.extend(relativeTime);

interface RefundLog {
  id: string;
  filing_id: string;
  company_name: string;
  org_name: string;
  refund_amount: number;
  reason: string;
  refunded_by_name: string;
  created_at: string;
}

export default function AdminRefundLogs() {
  const [searchTerm, setSearchTerm] = useState("");
  const [refundLogs, setRefundLogs] = useState<RefundLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchRefundLogs = useCallback(async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase.rpc('get_refund_logs');

      if (error) {
        console.error('Error fetching refund logs:', error);
        setRefundLogs([]);
        return;
      }

      setRefundLogs(data || []);
    } catch (error) {
      console.error('Error fetching refund logs:', error);
      setRefundLogs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRefundLogs();
  }, [fetchRefundLogs]);

  // Filter documents based on search
  const filteredLogs = refundLogs.filter((log) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      log.company_name?.toLowerCase().includes(searchLower) ||
      log.org_name?.toLowerCase().includes(searchLower) ||
      log.reason?.toLowerCase().includes(searchLower) ||
      log.refunded_by_name?.toLowerCase().includes(searchLower)
    );
  });

  // Pagination
  const totalPages = Math.ceil(filteredLogs.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentLogs = filteredLogs.slice(startIndex, endIndex);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <MainLayout>
      <div className="page-container">
        <div className="max-w-6xl mx-auto space-y-6">
          {/* Header */}
          <div className="page-header">
            <div className="page-header-left">
              <div className="text-sm text-gray-500 mb-1">Admin Only</div>
              <h1 className="page-title">Refund Logs</h1>
            </div>
            <div className="page-header-right">
              <button
                onClick={() => fetchRefundLogs()}
                className="f-btn f-btn-success"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh
              </button>
            </div>
          </div>

          {/* Main Content */}
          <div className="page-card">
            {/* Search */}
            <div className="flex items-center justify-between mb-6">
              <div className="relative flex-1 max-w-md">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="Search by company, organization, reason..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>
              <div className="text-sm text-gray-500">
                {filteredLogs.length} refund{filteredLogs.length !== 1 ? 's' : ''} total
              </div>
            </div>

            {/* Table */}
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-x-auto">
              <table className="page-table">
                <thead className="page-table-thead">
                  <tr className="page-table-tr">
                    <th className="page-table-th">Date</th>
                    <th className="page-table-th">Organization</th>
                    <th className="page-table-th">Company Name</th>
                    <th className="page-table-th">Amount</th>
                    <th className="page-table-th">Reason</th>
                    <th className="page-table-th">Refunded By</th>
                  </tr>
                </thead>
                <tbody className="page-table-tbody">
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="page-table-td text-center py-8">
                        <div className="flex items-center justify-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                          <span className="ml-2 text-gray-500">Loading refund logs...</span>
                        </div>
                      </td>
                    </tr>
                  ) : currentLogs.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="page-table-td text-center py-8 text-gray-500">
                        No refund logs found
                      </td>
                    </tr>
                  ) : (
                    currentLogs.map((log) => (
                      <tr key={log.id} className="page-table-tr hover:bg-gray-50">
                        <td className="page-table-td whitespace-nowrap">
                          {formatDate(log.created_at)}
                        </td>
                        <td className="page-table-td">
                          {log.org_name || '-'}
                        </td>
                        <td className="page-table-td">
                          {log.company_name || '-'}
                        </td>
                        <td className="page-table-td">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            +{log.refund_amount} credit{log.refund_amount !== 1 ? 's' : ''}
                          </span>
                        </td>
                        <td className="page-table-td max-w-xs">
                          <div className="truncate" title={log.reason}>
                            {log.reason || '-'}
                          </div>
                        </td>
                        <td className="page-table-td">
                          {log.refunded_by_name || '-'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {!loading && filteredLogs.length > 0 && (
              <div className="flex items-center justify-between text-sm text-gray-600 mt-6">
                <div className="flex items-center space-x-2">
                  <span>Rows per page:</span>
                  <select
                    value={rowsPerPage}
                    onChange={(e) => {
                      setRowsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="border border-gray-300 rounded px-2 py-1"
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                </div>
                <span>
                  {startIndex + 1}-{Math.min(endIndex, filteredLogs.length)} of {filteredLogs.length}
                </span>
                <div className="flex space-x-1">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                    disabled={currentPage === 1}
                    className="f-btn f-btn-soft f-btn-xs disabled:opacity-40"
                  >
                    &lt;
                  </button>
                  <button className="f-btn f-btn-primary f-btn-xs">
                    {currentPage}
                  </button>
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="f-btn f-btn-soft f-btn-xs disabled:opacity-40"
                  >
                    &gt;
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
